import React from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorProvider } from './error/ErrorContext';
import GlobalErrorBoundary from './error/GlobalErrorBoundary';
import { initializeGlobalErrorListeners } from './error/useGlobalErrorListeners';
import {
  reportNonAxiosMutationError,
  reportNonAxiosQueryError,
} from './services/errorHandling/nonAxiosErrorReporter.util';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './bootstrap.min.css';
import './jquery-ui-custom.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './ckeditor.css';
import './index.css';

// Initialize global error listeners BEFORE React renders
// This catches errors outside React (window.onerror, unhandledrejection)
initializeGlobalErrorListeners();



const queryClient = new QueryClient({
  // Axios failures are already logged with full request/response context by the
  // axios response interceptor (axiosConfig.ts) — this only catches a thrown bug
  // inside a queryFn/mutationFn/select that isn't a network failure at all (e.g.
  // a bad assumption about response shape). Log-only, no redirect.
  queryCache: new QueryCache({ onError: reportNonAxiosQueryError }),
  mutationCache: new MutationCache({ onError: reportNonAxiosMutationError }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: false,
      gcTime: 5 * 60 * 1000,
      staleTime: 30 * 1000,
      networkMode: 'always',
    },
    mutations: {
      networkMode: 'always',
    },
  },
});

const root = createRoot(document.querySelector('#root') as HTMLElement);
root.render(
  <React.StrictMode>
    <ErrorProvider>
      <GlobalErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </GlobalErrorBoundary>
    </ErrorProvider>
  </React.StrictMode>,
);

reportWebVitals();
